import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { hasAuthHint, setAuthHint } from "../utils/device";

import { syncCookieConsentToUser } from "../utils/cookieConsent";

/*
 * Session-based authentication.
 *
 * The server keeps the session and sets an httpOnly cookie; this app never
 * receives or stores a token. We only keep the signed-in user in memory and
 * a harmless "signed in here" hint so guests skip the verify call.
 */

const AuthContext = createContext(null);

const ALLOWED_ROLE = "customer";

// Admin app is on a completely different origin
const ADMIN_LOGIN = "https://console.grapheweddings.com/login";

/*
 * Thrown by login() when the account is already signed in on the maximum
 * number of devices. `data` holds the limit, the one-time challenge token
 * and the active sessions to choose from.
 */
export class DeviceLimitError extends Error {
  constructor(message, data) {
    super(message);
    this.name = "DeviceLimitError";
    this.code = "DEVICE_LIMIT_REACHED";
    this.data = data;
  }
}

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authChecking, setAuthChecking] = useState(false);

  const clearAuth = useCallback(() => {
    setAuthHint(false);
    setUser(null);
  }, []);

  const acceptUser = useCallback(
    (userData) => {
      if (!userData || userData.role !== ALLOWED_ROLE) {
        clearAuth();

        if (userData?.role === "admin") {
          window.location.replace(ADMIN_LOGIN);
        }

        return false;
      }

      setAuthHint(true);
      setUser(userData);
      return true;
    },
    [clearAuth]
  );

  const checkAuthStatus = useCallback(async () => {
    if (!hasAuthHint()) {
      setUser(null);
      return false;
    }

    try {
      const res = await api.get("/auth/verify", { skipAuthRedirect: true });
      return acceptUser(res.data?.data?.user);
    } catch {
      clearAuth();
      return false;
    }
  }, [acceptUser, clearAuth]);

  const initialiseAuth = useCallback(async () => {
    setAuthLoading(true);

    try {
      await checkAuthStatus();
    } finally {
      setAuthLoading(false);
    }
  }, [checkAuthStatus]);

  const silentCheckAuthStatus = useCallback(async () => {
    setAuthChecking(true);

    try {
      await checkAuthStatus();
    } finally {
      setAuthChecking(false);
    }
  }, [checkAuthStatus]);

  /*
   * Shared by login, login/resolve and register. Converts the server's
   * device-limit and wrong-app responses into meaningful errors.
   */
  const runAuthRequest = useCallback(
    async (request) => {
      let res;

      try {
        res = await request();
      } catch (error) {
        const data = error.response?.data;

        if (data?.code === "DEVICE_LIMIT_REACHED") {
          throw new DeviceLimitError(data.message, data.data);
        }

        if (data?.code === "WRONG_APP" && data.data?.role === "admin") {
          window.location.replace(ADMIN_LOGIN);
        }

        throw error;
      }

      if (!acceptUser(res.data?.data?.user)) {
        throw new Error("This website is only for customers.");
      }

      // Associate previously selected cookie consent with this user.
      try {
        await syncCookieConsentToUser();
      } catch (error) {
        console.error("Cookie consent sync failed:", error);
      }

      return res.data;
    },
    [acceptUser]
  );

  const login = ({ email, password }) =>
    runAuthRequest(() => api.post("/auth/login", { email, password }));

  // Finish a login that hit the device limit by signing out chosen devices.
  const resolveLogin = ({ challengeToken, revokeSessionUids }) =>
    runAuthRequest(() =>
      api.post("/auth/login/resolve", {
        challenge_token: challengeToken,
        revoke_session_uids: revokeSessionUids,
      })
    );

  const register = (formData) =>
    runAuthRequest(() =>
      api.post("/auth/register", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
    );

  const logout = async () => {
    try {
      await api.post("/auth/logout", null, { skipAuthRedirect: true });
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      clearAuth();

      navigate("/login", {
        replace: true,
      });
    }
  };

  useEffect(() => {
    initialiseAuth();
  }, [initialiseAuth]);

  // The API client reports a revoked/expired session; drop the user.
  useEffect(() => {
    const handleSessionEnded = () => setUser(null);

    window.addEventListener("auth-session-ended", handleSessionEnded);
    return () =>
      window.removeEventListener("auth-session-ended", handleSessionEnded);
  }, []);

  /**
   * Re-check the session when the tab becomes active again, so a device
   * signed out elsewhere notices promptly.
   */
  useEffect(() => {
    let timer;

    const run = () => {
      clearTimeout(timer);
      timer = setTimeout(silentCheckAuthStatus, 300);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") run();
    };

    window.addEventListener("focus", run);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("focus", run);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearTimeout(timer);
    };
  }, [silentCheckAuthStatus]);

  return (
    <AuthContext.Provider
      value={{
        user,
        role: user?.role || null,
        isAuthenticated: !!user,

        authLoading,
        authChecking,

        login,
        resolveLogin,
        register,
        logout,

        clearAuth,
        checkAuthStatus,
        silentCheckAuthStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
